import { DatePickerSelector } from '@/common/components/selectors/DatePickerSelector';
import { Planner, TodoWithDetails } from '@/common/types/databaseTypes';
import { CommentSection } from '@/features/comment/components/CommentSection';
import { Button, Stack } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import TodoActivityFeed from '../activity/TodoActivityFeed';
import { TodoBasicInfo, TodoMetadata } from './TodoContentCommon';
import TodoDescription from './TodoDescription';
import { useAuthStore } from '@/stores/authStore';
import { useCheckSameWorkspaceMembership } from '@/features/workspace/services/useWorkspaceQueries';

interface TodoDetailContentProps {
    todo: TodoWithDetails;
    onDateSelect: (date: Date) => Promise<void>;
    onPlannerSelect: (planner: Planner) => void;
    onDeletePlannerRange: (plannerId: string, rangeToDelete: number[]) => void;
    onContentChange?: (content: string) => void;
}

export const TodoDetailContent = ({
    todo,
    onDateSelect,
    onPlannerSelect,
    onDeletePlannerRange,
    onContentChange,
}: TodoDetailContentProps) => {
    const { t } = useTranslation('todo');
    const { user } = useAuthStore();

    const workspaceMembership = useCheckSameWorkspaceMembership(
        todo.user_id,
        user.id
    );

    const handleDatePickerChange = (date: Date | null) => {
        if (date) {
            void onDateSelect(date);
        }
    };

    return (
        <Stack p="md" fz="sm" c="gray.7">
            <TodoBasicInfo todo={todo} size="md" variant="unstyled" />
            <TodoDescription todo={todo} onContentChange={onContentChange} />
            <TodoMetadata
                todo={todo}
                onPlannerSelect={onPlannerSelect}
                onDeletePlannerRange={onDeletePlannerRange}
            />
            {todo.user_id === user.id || workspaceMembership.data?.isSameWorkspace ? (
                <DatePickerSelector
                    actionIcon={
                        <Button
                            fz="sm"
                            leftSection={<IconPlus size="16" />}
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            fullWidth
                        >
                            {t('component.todoDrawer.detail.add_date')}
                        </Button>
                    }
                    selectedDate={null}
                    onDateChange={handleDatePickerChange}
                    showTodayButton={false}
                    showSelectedDate={false}
                />
            ) : null}
            <TodoActivityFeed todo={todo} />
            <CommentSection todoId={todo.id} />
        </Stack>
    );
};
